'use client'

import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { alpha, Box, Typography } from '@mui/material'
import PublicIcon from '@mui/icons-material/Public'
import DoneIcon from '@mui/icons-material/Done'

import { AccountSettingsItem } from '@/src/types/account'
import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser, clearAuthUser } from '@/src/redux/slices/authSlice'
import { setCurrency } from '@/src/redux/slices/currencySlice'
import { useUserLogoutMutation } from '@/src/api/rtkQuery/userApi'
import { deleteCookie } from '@/src/utils/browser'
import { languageOptions, currencyOptions } from '@/src/constants/header'
import { useGetCurrencyDataQuery } from '@/src/api/rtkQuery/currencyApi'
import { getLanguageByCode, setLanguageCookie } from '@/src/utils/language'
import { mapApiCurrencyOptions } from '@/src/utils/currency'
import { getBrandName } from '@/src/utils/buildUtils'
import { setCookie } from '@/src/utils/browser'
import {
    getCountryOptions,
    detectCurrentCountry,
    type CountryOption
} from '@/src/utils/countryOptions'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer/swipeableDrawer'

// Dynamically load SignInModal to reduce initial bundle size
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/header/variants/flyin/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

import { AccountPage, type AccountMenuItem } from './account'
import BottomToolbarWrapper from '../bottomToolbar'
import { getFooterLinksByBrand, getLinkByName } from '../homepageFooter/footerLinks'

const APP_VERSION = '25.2.72'

const isDisplayNameFirstName = (displayName: string | undefined): boolean => {
    if (!displayName || displayName === 'User') return false
    // Check if it's an email
    if (displayName.includes('@') || displayName.includes('.')) return false
    // Check if it's a mobile number (starts with + or contains any digits)
    if (displayName.startsWith('+') || /\d/.test(displayName)) return false
    return true
}

interface AccountPageWrapperProps {
    showVisa?: boolean
    showFlightsHotels?: boolean
    initialIsLoggedIn?: boolean
    initialUsername?: string
    initialGreetingName?: string
}

const AccountPageWrapper: React.FC<AccountPageWrapperProps> = ({
    showVisa,
    showFlightsHotels,
    initialIsLoggedIn = false,
    initialUsername = 'User',
    initialGreetingName = 'User'
}) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('common')
    const tFooter = useTranslations('footer')
    const dispatch = useAppDispatch()

    // Track if component is mounted (client-side only)
    const [isMounted, setIsMounted] = useState(false)

    // Drawer state
    const [languageDrawerOpen, setLanguageDrawerOpen] = useState(false)
    const [currencyDrawerOpen, setCurrencyDrawerOpen] = useState(false)
    const [countryDrawerOpen, setCountryDrawerOpen] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)

    // Selected language and currency (using utility)
    const [selected, setSelected] = useState({
        language: getLanguageByCode(locale),
        currency: currencyOptions[0] // Default to first option to avoid hydration mismatch
    })

    // Fetch currency list from API
    const { data: currencyData, isLoading: isCurrencyLoading, isError: isCurrencyError } = useGetCurrencyDataQuery()

    // Map API data to expected shape for dropdown (using utility)
    const apiCurrencyOptions = React.useMemo(() => mapApiCurrencyOptions(currencyData), [currencyData])

    const currency = useAppSelector(state => state.currency.selected)
    const user = useAppSelector(selectAuth)
    const brand = getBrandName() as 'flyin' | 'cleartrip'
    const countryOptions = React.useMemo(() => getCountryOptions(t, brand), [t, brand])
    const isQA =
        typeof window !== 'undefined' &&
        window.location.hostname.includes('me')

    // Use isMounted to prevent hydration mismatch - only use user data after client-side mount
    // During SSR and initial render, use server-provided values that match server output
    const isLoggedIn = isMounted ? user.signedIn : initialIsLoggedIn

    const emailPrefix = user.email?.split('@')[0]
    const username = isMounted
        ? user.firstName || user.displayName || emailPrefix || 'User'
        : initialUsername

    // For greeting: use firstName if available, else displayName only if it looks like firstName,
    // else fall back to email prefix (if available), else 'User'
    const greetingName = isMounted
        ? user.firstName || (isDisplayNameFirstName(user.displayName) ? user.displayName : emailPrefix || 'User')
        : initialGreetingName
    const hideSignIn = false
    const { companyLinks, supportLinks } = getFooterLinksByBrand(tFooter, locale)

    // Initialize on mount (client-side only)
    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    // Update currency from Redux store when mounted
    useEffect(() => {
        if (!isMounted) return

        // Update currency if needed
        if (!isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0) {
            const found = apiCurrencyOptions.find((opt: any) => opt.code === currency)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        }
    }, [isMounted, currency, apiCurrencyOptions, isCurrencyLoading, isCurrencyError])

    // Sync language selection with locale changes (using utility)
    useEffect(() => {
        const langOption = getLanguageByCode(locale)
        setSelected(prev => ({
            ...prev,
            language: langOption
        }))
    }, [locale])

    // Selected country - detect from hostname on mount
    const [selectedCountry, setSelectedCountry] = useState<CountryOption>(countryOptions[0])
    useEffect(() => {
        if (!isMounted || typeof window === 'undefined') return
        const isQADomain =
            window.location.hostname.includes('me')
        const current = detectCurrentCountry(window.location.hostname, countryOptions, isQADomain, brand)
        setSelectedCountry(current)
    }, [isMounted, countryOptions, brand])

    // Navigation handlers
    const handleMenuClick = useCallback(
        (path: string) => {
            router.push(path)
        },
        [router]
    )

    const handleMenuLink = useCallback((url: string) => {
        window.location.href = url
    }, [])

    const handlePrivacyClick = useCallback(() => {
        window.location.href = getLinkByName('privacyPolicy', 'company', tFooter) || `/privacyPolicy.${locale}.html`
    }, [locale])

    const handleTermsClick = useCallback(() => {
        window.location.href = getLinkByName('termsOfUse', 'company', tFooter) || `/termsOfUse.${locale}.html`
    }, [locale])

    const handleSupportClick = useCallback(() => {
        window.location.href = getLinkByName('contactUs', 'support', tFooter) || `/${locale}/contact`
    }, [locale, tFooter])

    // Modal handlers
    const handleModalOpen = useCallback((modal: 'signIn') => {
        if (modal === 'signIn') setModalOpen(true)
    }, [])

    const handleModalClose = useCallback(() => {
        setModalOpen(false)
    }, [])

    // Language change handler
    const handleLanguageChange = useCallback(
        (lang: (typeof languageOptions)[0]) => {
            if (lang.code === selected.language.code) {
                // Already on this language, just close drawer
                setLanguageDrawerOpen(false)
                return
            }

            // Set language cookie
            setLanguageCookie(lang.code)

            const currentPath = window.location.pathname
            const currentSearch = window.location.search
            const currentHash = window.location.hash

            let newPath = currentPath
            if (currentPath.startsWith('/en/') || currentPath === '/en') {
                newPath = currentPath.replace(/^\/en/, `/${lang.code}`)
            } else if (currentPath.startsWith('/ar/') || currentPath === '/ar') {
                newPath = currentPath.replace(/^\/ar/, `/${lang.code}`)
            } else {
                newPath = `/${lang.code}${currentPath}`
            }

            window.location.href = newPath + currentSearch + currentHash
        },
        [selected.language.code]
    )

    // Currency change handler
    const handleCurrencyChange = useCallback(
        (curr: (typeof currencyOptions)[0]) => {
            setSelected(prev => ({ ...prev, currency: curr }))
            dispatch(setCurrency({ code: curr.code, symbol: curr.symbol }))
            setCurrencyDrawerOpen(false)
        },
        [dispatch]
    )

    // Country change handler - redirects to new domain
    const handleCountryChange = useCallback(
        (country: CountryOption) => {
            if (country.code === selectedCountry.code) {
                setCountryDrawerOpen(false)
                return
            }
            setSelectedCountry(country)
            if (brand === 'flyin') {
                setCookie('cc', country.code, undefined, '.flyin.com')
            }
            const targetDomain = isQA ? country.qaDomain : country.domain
            const currentUrl = new URL(window.location.href)
            currentUrl.hostname = targetDomain
            currentUrl.protocol = window.location.protocol
            window.location.href = currentUrl.toString()
        },
        [selectedCountry.code, brand, isQA]
    )

    // Sign out handler
    const [triggerLogout] = useUserLogoutMutation()
    const handleSignOut = useCallback(async () => {
        try {
            await triggerLogout({ brand: getBrandName() }).unwrap()
            dispatch(clearAuthUser())
            const domain = '.' + window.location.hostname
            deleteCookie('usermisc', domain)
            deleteCookie('userid', domain)
            deleteCookie('userData', domain)
            dispatch(refreshAuthUser())
            router.push(`/${locale}`)
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }, [dispatch, triggerLogout, router, locale])

    const walletHref = `/${locale}/wallet`

    const settingsItems: AccountSettingsItem[] = [
        {
            id: 'country',
            label: t('country'),
            rightContent: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedCountry.Flag ? (
                        <selectedCountry.Flag width={20} height={15} style={{ borderRadius: 2 }} />
                    ) : (
                        <PublicIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600 }}>
                        {selectedCountry.label}
                    </Typography>
                </Box>
            ),
            onClick: () => setCountryDrawerOpen(true)
        },
        {
            id: 'currency',
            label: t('currency'),
            rightContent: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <selected.currency.Flag width={20} height={15} style={{ borderRadius: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600 }}>
                        {selected.currency.code}
                    </Typography>
                </Box>
            ),
            onClick: () => setCurrencyDrawerOpen(true)
        },
        {
            id: 'language',
            label: t('language'),
            rightContent: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600 }}>
                        {selected.language.label}
                    </Typography>
                </Box>
            ),
            onClick: () => setLanguageDrawerOpen(true)
        }
    ]

    const handleGateNavigation = useCallback(
        (path: string) => {
            if (isLoggedIn) {
                handleMenuClick(path)
            } else {
                handleModalOpen('signIn')
            }
        },
        [isLoggedIn, handleMenuClick, handleModalOpen]
    )

    const menuItems = useMemo<AccountMenuItem[]>(
        () => [
            { id: 'bookings', label: t('bookings'), onClick: () => handleGateNavigation(`/${locale}/trips`) },
            { id: 'wallet', label: t('wallet'), onClick: () => handleGateNavigation(walletHref) },
            { id: 'help', label: t('helpAndSupport'), onClick: handleSupportClick },
            { id: 'profile', label: t('profile'), onClick: () => handleGateNavigation(`/${locale}/profile`) },
            { id: 'travellers', label: t('travellersMenu'), onClick: () => handleGateNavigation(`/${locale}/travellers`) },
            { id: 'signOut', label: t('signOut'), onClick: handleSignOut },
            { id: 'privacy', label: t('privacyPolicy'), onClick: handlePrivacyClick },
            { id: 'terms', label: t('termsOfUse'), onClick: handleTermsClick }
        ],
        [t, locale, walletHref, handleGateNavigation, handleSupportClick, handleSignOut, handlePrivacyClick, handleTermsClick]
    )

    const labels = useMemo(
        () => ({
            hello: t('hello'),
            yourProfile: t('yourProfile'),
            manageYourAccount: t('manageYourAccount'),
            logInToUnlock: t('logInToUnlock'),
            logInSignUp: t('logInSignUp'),
            safeSecure: t('safeSecureTransaction'),
            version: t('version')
        }),
        []
    )

    return (
        <>
            <AccountPage
                isLoggedIn={isLoggedIn}
                labels={labels}
                menuItems={menuItems}
                settingsItems={settingsItems}
                appVersion={APP_VERSION}
                username={username}
                greetingName={greetingName}
                hideSignIn={hideSignIn}
                onModalOpen={handleModalOpen}
            />

            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}

            {/* Language Drawer */}
            <SwipeableDrawerWithBackNavigation
                open={languageDrawerOpen}
                onClose={() => setLanguageDrawerOpen(false)}
                sx={{ height: '30dvh', maxHeight: '30dvh' }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t('chooseLanguage')}
                    </Typography>
                    {languageOptions.map(lang => {
                        const isSelected = lang.code === selected.language.code
                        return (
                            <Box
                                key={lang.code}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    mb: 1,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: isSelected
                                        ? theme => alpha(theme.palette.primary.main, 0.08)
                                        : 'background.paper',
                                    border: 1,
                                    borderColor: 'divider',
                                    '&:hover': {
                                        bgcolor: isSelected
                                            ? theme => alpha(theme.palette.primary.main, 0.12)
                                            : 'action.hover'
                                    }
                                }}
                                onClick={() => handleLanguageChange(lang)}
                            >
                                <Typography variant="body1" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                    {lang.label}
                                </Typography>
                                {isSelected && <DoneIcon color="primary" sx={{ ml: 'auto' }} />}
                            </Box>
                        )
                    })}
                </Box>
            </SwipeableDrawerWithBackNavigation>

            {/* Country Drawer */}
            <SwipeableDrawerWithBackNavigation
                open={countryDrawerOpen}
                onClose={() => setCountryDrawerOpen(false)}
                sx={{ height: '50dvh', maxHeight: '50dvh' }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t('chooseCountry')}
                    </Typography>
                    {countryOptions.map(country => {
                        const isSelected = country.code === selectedCountry.code
                        const CountryFlag = country.Flag
                        return (
                            <Box
                                key={country.code}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    mb: 1,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: isSelected
                                        ? theme => alpha(theme.palette.primary.main, 0.08)
                                        : 'background.paper',
                                    border: 1,
                                    borderColor: 'divider',
                                    '&:hover': {
                                        bgcolor: isSelected
                                            ? theme => alpha(theme.palette.primary.main, 0.12)
                                            : 'action.hover'
                                    }
                                }}
                                onClick={() => handleCountryChange(country)}
                            >
                                {CountryFlag ? (
                                    <CountryFlag width={24} height={18} style={{ borderRadius: 2 }} />
                                ) : (
                                    <PublicIcon sx={{ width: 24, height: 18, color: 'text.secondary' }} />
                                )}
                                <Typography variant="body1" sx={{ ml: 1.5, fontWeight: isSelected ? 600 : 400 }}>
                                    {country.label}
                                </Typography>
                                {isSelected && <DoneIcon color="primary" sx={{ ml: 'auto' }} />}
                            </Box>
                        )
                    })}
                </Box>
            </SwipeableDrawerWithBackNavigation>

            {/* Currency Drawer */}
            <SwipeableDrawerWithBackNavigation open={currencyDrawerOpen} onClose={() => setCurrencyDrawerOpen(false)}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t('chooseCurrency')}
                    </Typography>
                    {(!isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0
                        ? apiCurrencyOptions
                        : currencyOptions
                    ).map((curr: any) => {
                        const isSelected = curr.code === selected.currency.code
                        return (
                            <Box
                                key={curr.code}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    mb: 1,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: isSelected
                                        ? theme => alpha(theme.palette.primary.main, 0.08)
                                        : 'background.paper',
                                    border: 1,
                                    borderColor: 'divider',
                                    '&:hover': {
                                        bgcolor: isSelected
                                            ? theme => alpha(theme.palette.primary.main, 0.12)
                                            : 'action.hover'
                                    }
                                }}
                                onClick={() => handleCurrencyChange(curr)}
                            >
                                <curr.Flag width={24} height={18} style={{ borderRadius: 2 }} />
                                <Typography variant="body1" sx={{ ml: 1.5, fontWeight: isSelected ? 600 : 400 }}>
                                    {curr.label} ({curr.code})
                                </Typography>
                                {isSelected && <DoneIcon color="primary" sx={{ ml: 'auto' }} />}
                            </Box>
                        )
                    })}
                </Box>
            </SwipeableDrawerWithBackNavigation>

            <BottomToolbarWrapper showVisa={showVisa} showFlightsHotels={showFlightsHotels} />
        </>
    )
}

export { AccountPageWrapper }
export default AccountPageWrapper
